import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchApi } from "../../api/convex";
import { PreviewLoadingCard } from "../../components/PreviewLoadingCard";
import { setOnboardingContext } from "../../lib/onboardingStorage";

const LOADING_STEPS = [
  "searchLoading1",
  "searchLoading2",
  "searchLoading3",
  "searchLoading4",
  "searchLoading5",
];

export function OnboardingProcessingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const handle = searchParams.get("handle") ?? "";

  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!handle || startedRef.current) return;
    startedRef.current = true;

    const previewSessionId = `web_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

    // Animate steps while fetching
    let step = 0;
    const stepInterval = setInterval(() => {
      step++;
      setCurrentStep(step);
      if (step >= LOADING_STEPS.length - 1) {
        clearInterval(stepInterval);
      }
    }, 600);

    // Fetch preview data using obfuscated endpoint
    fetchApi("/api/v1/preview", {
      username: handle,
      previewSessionId,
    })
      .then((result: any) => {
        clearInterval(stepInterval);
        setCurrentStep(LOADING_STEPS.length);

        const isPrivate = result?.isPrivate || result?.is_private;
        if (isPrivate) {
          setError("This profile is private and cannot be monitored.");
          return;
        }

        // Store context in sessionStorage
        setOnboardingContext({
          username: handle,
          previewSessionId,
          monitorId: null,
          previewData: {
            username: handle,
            fullName: result?.fullName ?? result?.full_name,
            profilePicUrl: result?.profilePicUrl ?? result?.profile_pic_url,
            biography: result?.biography,
            followingCount: result?.followingCount ?? result?.following_count ?? 0,
            followersCount: result?.followersCount ?? result?.followers_count ?? 0,
            femaleCount: result?.femaleCount ?? result?.female_count ?? 0,
            maleCount: result?.maleCount ?? result?.male_count ?? 0,
            followings: (result?.followings ?? result?.previewFollowings ?? []).map((f: any) => ({
              username: f.username ?? f.handle ?? "",
              fullName: f.fullName,
              profilePicUrl: f.profilePicUrl,
              gender: f.gender ?? "unknown",
            })),
          },
        });

        // Navigate to preview after minimum 1.5s
        setTimeout(() => {
          navigate(`/onboarding/preview?handle=${encodeURIComponent(handle)}`);
        }, 300);
      })
      .catch((err: any) => {
        clearInterval(stepInterval);

        const message = err?.message ?? String(err);
        if (message.toLowerCase().includes("private")) {
          setError("This profile is private and cannot be monitored.");
        } else if (message.toLowerCase().includes("not found")) {
          setError("Profile not found. Check the handle and try again.");
        } else {
          setError("Something went wrong. Please try again.");
        }
      });

    return () => clearInterval(stepInterval);
  }, [handle, navigate]);

  const progress = Math.min(
    0.96,
    Math.max(0.08, currentStep / LOADING_STEPS.length),
  );

  return (
    <div className="st-page st-preview-page st-onboarding-processing-page">
      <main className="st-section st-preview-results st-preview-loading-view">
        <div className="st-container">
          {error ? (
            <article className="st-preview-state-card st-preview-error-card">
              <h2>Cannot monitor this profile</h2>
              <p>{error}</p>
              <div className="st-preview-error-actions">
                <button
                  onClick={() => navigate("/onboarding/target")}
                  className="st-preview-primary-button"
                >
                  Try another handle
                </button>
              </div>
            </article>
          ) : (
            <PreviewLoadingCard
              handle={handle}
              progress={progress}
              steps={LOADING_STEPS}
            />
          )}
        </div>
      </main>
    </div>
  );
}
